import { HttpClientModule, HttpClientJsonpModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './components/app/app.component';
import { SplitWordsPipe } from './pipes/split-words.pipe';
import { UtcToLocalePipe } from './pipes/utc-to-locale.pipe';
import { HideByAccessDirective, LabelControlDirective, KeyUpDirective, DisableControlDirective } from './shared/directives/index';
import { CompanyAuthorizationGuard, ContactAuthorizationGuard, LLFormAuthorizationGuard, LLFormGuard, ERFormCDGuard, ERFormCAGuard, BusinessCategoryAuthorizationGuard } from './shared/guards/index';
import { ContactEditGuard } from './shared/guards/contact-edit-guard';
import { CompanyResolverService, LLFormResolverService, ERFormResolverService, OrderResolverService, LLDashboardResolverService, ERDashboardResolverService, LCRDashboardResolverService, BusinessCategoryResolverService } from './shared/resolvers/index';
import { AuthenticationService } from './shared/services/authentication.service';
import { AuthorizationService, CustomerService, LoggingService, SettingsService, StorageManagerService, TemplateService, CompanyContactService, LLDashboardService} from './shared/services/index';
import { SessionTimeoutService } from './shared/services/session-timeout.service';
import { RefdataService } from './shared/services/refdata.service';
import { BlockUIModule } from 'ng-block-ui';
import { LLFormService } from './shared/services/ll-form.service';
import { OrderService } from './shared/services/order.service';
import { Utilities } from './shared/services/utilities';
import {
  AccessdeniedComponent, CustomerSelectorComponent, UnauthorizedComponent,
  ForbiddenComponent, FooterComponent, HomeComponent, NavMenuComponent, SupportComponent, ContactDetailComponent, CompanyDetailsComponent, ContactComponent,
  CompanyDashboardComponent, ContactDashboardComponent,
  CreateLLFormComponent, ERFormComponent, ERFormDashboardComponent,
  OrderStep1Component, OrderStep2Component, OrderStep3Component, LCRFormDashboardComponent,
  OrderSummaryComponent, OrderTrackerComponent, OrderAdminComponent, NoEsgAccessModalComponent } from './components/index';
import { DatePipe } from '@angular/common';
import { AbsoluteSessionTimeoutService } from './shared/services/session-timeout-absolute.service';
import { NavigatorComponent } from './components/navigator/navigator.component';
import { LCRFormComponent } from './components/lcr-form/lcr-form.component';
import { SanitizeHtmlPipe } from './pipes/sanitize-html.pipe';
import { LCRFormAuthorizationGuard } from './shared/guards/lcr-form-authorization.guard';
import { LCRFormResolverService } from './shared/resolvers/lcr-form-resolver.service';
import { LCRFormGuard } from './shared/guards/lcr-form-guard';
import { LCRFormService } from './shared/services/lcr-form.service';
import { ERFormService } from './shared/services/er-form-service';
import { OrderGuard } from './shared/guards/order-guard';
import { OrderStep1Guard } from './shared/guards/order-step-1-guard';
import { OrderStep2Guard } from './shared/guards';
import { OrderStep3Guard } from './shared/guards/order-step-3-guard';
import { SubmissionAuthorizationGuard } from './shared/guards/submission-authorization.guard';
import { LlValidationService } from './shared/services/ll-validation.service';
import { LCRValidationService } from './shared/services/lcr-validation.service';
import { JwtInterceptor } from './shared/helpers/jwt.interceptor';
import { BusinessCategoryComponent } from './components/business-category/business-category.component';
import { BusinessCategoryEditGuard } from './shared/guards/business-category.guard';
import { HealthCanadaSplValidationComponent } from './components/health-canada-spl-validation/health-canada-spl-validation.component';
import { HCSplValidationService } from './shared/services/hc-spl-validation.service';
import { IRevisionHistory } from './shared/models/revisionHistory.model';
import { DeleteFormService } from './shared/services/delete-form.service';
import { RTSharedModule } from './rt-shared.module';
import { SiteLayoutComponent } from './components/_layout/site-layout/site-layout.component';
import { SplViewerComponent } from './components/spl-viewer/spl-viewer.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { AuthConfigModule } from './auth-config.module';
import { ESGSubmissionService } from './shared/services/esg-submission-service';
import { SubmissionDashboardComponent } from './components/submission-dashboard/submission-dashboard.component';
import { SubmissionDashboardService } from './shared/services/submission-dashboard.service';
import { SubstanceLookupComponent } from './components/substance-lookup/substance-lookup.component';
import { SubstanceLookupService } from './shared/services/substance-lookup.service';
import { SubstanceLookupGuard } from './shared/guards/substance-lookup.guard';
import { GridPreferencesService } from './shared/services/grid-preferences.service';
import { PragmaticValidationService } from './shared/services/pragmatic-validation.service';
import { ColumnChooserService } from '@syncfusion/ej2-angular-grids';
import { SplUploadGuard } from './shared/guards/spl-upload.guard';
import { SplUploadComponent } from './components/areas/spl-upload/spl-upload.component';
import { FeatureService } from './shared/services/feature.service';
import { FileUploadHistoryGridComponent } from './components/grids/fileuploadhistorygrid/fileuploadhistorygrid.component';
import { FileUploadService } from './shared/services/file-upload.service';
import { SplUploadResolverService } from './shared/resolvers/spl-upload.resolver';
import { AccordionModule } from '@syncfusion/ej2-angular-navigations';
import { OrderComponent } from './components/order/order.component';
import { NavDLService } from './shared/services/navdl.service';

@NgModule({
  declarations: [
    AppComponent,
    AccessdeniedComponent,
    FooterComponent,
    HomeComponent,
    NavMenuComponent,
    SupportComponent,
    NavigatorComponent,
    CustomerSelectorComponent,
    SplitWordsPipe,
    UtcToLocalePipe,
    SanitizeHtmlPipe,
    HideByAccessDirective,
    DisableControlDirective,
    LabelControlDirective,
    KeyUpDirective,
    UnauthorizedComponent,
    ForbiddenComponent,
    NoEsgAccessModalComponent,
    ContactDetailComponent,
    ContactComponent,
    CompanyDashboardComponent,
    ContactDashboardComponent,
    SubmissionDashboardComponent,
    CreateLLFormComponent,
    OrderComponent,
    OrderStep1Component,
    OrderStep2Component,
    OrderStep3Component,
    OrderSummaryComponent,
    OrderTrackerComponent,
    OrderAdminComponent,
    LCRFormComponent,
    LCRFormDashboardComponent,
    ERFormComponent,
    ERFormDashboardComponent,
    BusinessCategoryComponent,
    HealthCanadaSplValidationComponent,
    SiteLayoutComponent,
    SplViewerComponent,
    PageNotFoundComponent,
    SubstanceLookupComponent,
    FileUploadHistoryGridComponent,
    SplUploadComponent,
  ],
  imports: [
    RTSharedModule,
    BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
    AuthConfigModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AccordionModule,
    AppRoutingModule,
    HttpClientJsonpModule,
    ToastrModule.forRoot({
      timeOut: 8000,
      preventDuplicates: true,
      positionClass: 'toast-top-center',
    }),
    BlockUIModule.forRoot()
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    StorageManagerService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    SettingsService,
    AuthenticationService,
    LoggingService,
    LLDashboardService,
    CustomerService,
    TemplateService,
    CompanyResolverService,
    LLFormResolverService,
    LLDashboardResolverService,
    LCRFormResolverService,
    LCRDashboardResolverService,
    BusinessCategoryResolverService,
    LCRValidationService,
    ERFormResolverService,
    AuthorizationService,
    LlValidationService,
    ERDashboardResolverService,
    ContactEditGuard,
    ContactAuthorizationGuard,
    CompanyAuthorizationGuard,
    LLFormAuthorizationGuard,
    LCRFormAuthorizationGuard,
    BusinessCategoryEditGuard,
    BusinessCategoryAuthorizationGuard,
    LLFormGuard,
    LCRFormGuard,
    ERFormCAGuard,
    ERFormCDGuard,
    SessionTimeoutService,
    AbsoluteSessionTimeoutService,
    Utilities,
    CompanyContactService,
    LLFormService,
    LCRFormService,
    ERFormService,
    RefdataService,
    DatePipe,
    OrderService,
    SubmissionDashboardService,
    OrderResolverService,
    OrderGuard,
    OrderStep1Guard,
    OrderStep2Guard,
    OrderStep3Guard,
    SubmissionAuthorizationGuard,
    LCRValidationService,
    LlValidationService,
    HCSplValidationService,
    IRevisionHistory,
    DeleteFormService,
    ESGSubmissionService,
    SubstanceLookupService,
    SubstanceLookupGuard,
    GridPreferencesService,
    PragmaticValidationService,
    ColumnChooserService,
    FeatureService,
    SplUploadGuard,
    FileUploadService,
    SplUploadResolverService,
    NavDLService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
